import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { AppContext } from "../../../context/appContext";
import { PrintStatementWrapper } from "./styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseInput } from "../../../components/form/input/styled";
import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	IconButton,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { printCustomerAccountStatementService } from "../../../utils/api/customerservice/printstatement/printCustomerAccountStatement";
import { PrintStatementTable } from "../../../containers/tables/printstatementtable";
import { BaseButton } from "../../../components/button/styled";
import {
	AlertModalCheckIcon,
	PrintIcon,
	VisibilityIcon,
	NonVisibilityIcon,
} from "../../../assets";
import { BaseAlertModal } from "../../../components/modal/alert";
import { emailCustomerAccountStatementService } from "../../../utils/api/customerservice/printstatement/emailCustomerAccountStatement";
import { useDebounce } from "use-debounce";
import { retrieveSavingsAccountByAccountNumberService } from "../../../utils/outbound/savingsaccount/retrieveSavingsAccountByAccountNumber";
import { retrieveCustomerService } from "../../../utils/api/customerservice/customerlist/retrieveCustomer";
import { formatAmountDisplay } from "../../../helpers/formatAmountDisplay";
import { retrieveInstitutionByIdService } from "../../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCurrencyByIdService } from "../../../utils/outbound/currency/retrieveCurrencyById";
import { retrieveUserProfileService } from "../../../utils/api/usermanagement/profile/retrieveUser";
import baseLogo from "../../../assets/icons/sidenavigationappicon.svg";
import { formatDate } from "../../../helpers/dateFormatter";
import { useReactToPrint } from "react-to-print";
import { retrieveBranchService } from "../../../utils/api/configuration/branch/retrieveBranch";

export const PrintStatement = () => {
	const tableHeaders = [
		"Transaction Date",
		"Description",
		"Debit",
		"Credit",
		"Balance",
	];

	const printableStatementTableHeaders = [
		"Date",
		"Description",
		"Debit",
		"Credit",
		"Balance",
	];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const contentRef = useRef<HTMLDivElement>(null);
	const { setActiveNavItem } = useContext(AppContext);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isTableVisible, setIsTableVisible] = useState(false);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
	const [institution, setInstitution] = useState({} as Record<string, any>);
	const [customerAccountDetails, setCustomerAccountDetails] = useState<any>({});
	const [formDetails, setFormDetails] = useState({
		accountNumber: "",
		startDate: "",
		endDate: "",
		email: "",
	});
	const [debouncedAccountRef] = useDebounce(formDetails.accountNumber, 500);

	useEffect(() => {
		setActiveNavItem("Customer Service");
	}, [setActiveNavItem]);

	useEffect(() => {
		if (!debouncedAccountRef) return;
		const controller = new AbortController();
		let isActive = true;
		const fetchBankAccountInfo = async () => {
			try {
				setIsLoadingCustomer(true);
				setCustomerAccountDetails({});
				const savingsAccount =
					await retrieveSavingsAccountByAccountNumberService(
						TOKEN,
						debouncedAccountRef,
						controller.signal,
					);
				const branch = await retrieveBranchService(
					TOKEN,
					savingsAccount?.branchId,
				);
				if (!isActive) return;
				if (!savingsAccount) {
					setCustomerAccountDetails({});
					return;
				}
				const customer = await retrieveCustomerService(
					TOKEN,
					savingsAccount.ownerId,
					controller.signal,
				);
				if (!isActive) return;
				setCustomerAccountDetails({
					...savingsAccount,
					customerName: `${customer?.firstName ?? ""} ${
						customer?.lastName ?? ""
					}`,
					customerBranch: branch?.name,
					customerAddress: customer?.address,
				});
			} catch (err: any) {
				if (err.name !== "AbortError") {
					console.error("Failed to fetch customer:", err);
					setCustomerAccountDetails({});
				}
			} finally {
				if (isActive) setIsLoadingCustomer(false);
			}
		};
		fetchBankAccountInfo();
		return () => {
			isActive = false;
			controller.abort();
		};
	}, [TOKEN, debouncedAccountRef]);

	const { data: transactingTeller } = useQuery({
		queryKey: ["transactingTeller", TOKEN],
		queryFn: async () => {
			return await retrieveUserProfileService(TOKEN);
		},
		enabled: !!TOKEN,
	});

	const { data: currency } = useQuery({
		queryKey: [
			"transactingCurrencySymbol",
			transactingTeller?._id,
			transactingTeller?.institution,
		],
		queryFn: async () => {
			if (!transactingTeller) return null;
			const institution = await retrieveInstitutionByIdService(
				transactingTeller._id,
				transactingTeller.institution,
			);
			const currencyDetails = await retrieveCurrencyByIdService(
				transactingTeller._id,
				institution?.currency,
			);
			return currencyDetails;
		},
		enabled: !!transactingTeller,
	});

	const { data: accountStatement } = useQuery({
		queryKey: [
			`accountStatement`,
			formDetails.startDate,
			formDetails.endDate,
			formDetails.accountNumber,
			TOKEN,
		],
		queryFn: async () => {
			const response = await printCustomerAccountStatementService(
				TOKEN,
				formDetails.accountNumber,
				{ startDate: formDetails.startDate, endDate: formDetails.endDate },
			);
			return response;
		},
		enabled: !!TOKEN && !!formDetails.accountNumber.trim(),
	});

	const accountStatementWithBalance = useMemo(() => {
		if (!accountStatement || customerAccountDetails?.balance == null) return [];

		const postedTransactions = [...accountStatement]
			.filter((txn) => txn.status === "approved")
			.sort(
				(a, b) =>
					new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
			);
		let balance = Number(customerAccountDetails.balance);

		return postedTransactions.map((txn) => {
			const row = { ...txn, balance };
			const amount = Number(txn.amount) || 0;
			// undo a debit by adding it; undo a credit by subtracting it.
			balance += txn.entryType === "dr" ? amount : -amount;
			return row;
		});
	}, [accountStatement, customerAccountDetails?.balance]);

	const { data: loggedInUser } = useQuery({
		queryKey: ["loggedInUser", TOKEN],
		queryFn: async () => {
			return await retrieveUserProfileService(TOKEN);
		},
		enabled: !!TOKEN,
	});

	useEffect(() => {
		const fetchInstitution = async () => {
			if (!loggedInUser) return;
			try {
				const institution = await retrieveInstitutionByIdService(
					loggedInUser?._id,
					loggedInUser?.institution,
				);
				return setInstitution(institution);
			} catch (error) {
				return console.error("Failed to fetch user institution:", error);
			}
		};
		fetchInstitution();
	}, [loggedInUser]);

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		return setIsAlertModalOpen(false);
	};

	const handleToggleTableVisibility = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		setIsTableVisible((prev) => !prev);
	};

	const handleSendAccountStatementToEmail = async (
		e: React.FormEvent<HTMLFormElement>,
	) => {
		e.preventDefault();
		setError(null);
		if (!accountStatement) return;
		setIsLoading(true);
		try {
			const response = await emailCustomerAccountStatementService(
				TOKEN,
				formDetails,
				{ startDate: formDetails.startDate, endDate: formDetails.endDate },
			);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
			} else {
				setIsLoading(false);
				setError(
					"Failed to send account statement to email. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Account statement failed to send to email. ${error.message}`);
			console.error("Account statement failed to send to email:", error);
		}
	};

	const handlePrintAccountStatement = useReactToPrint({
		contentRef,
		documentTitle: "Account Statement",
	});

	const PrintStyles = () => (
		<style>
			{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Poppins:wght@400;500;600;700&display=swap');
                
                @media print {
                    * {
                        font-family: 'Poppins', 'Inter', sans-serif !important;
                    }
                }
          `}
		</style>
	);

	const dateFrom =
		formDetails.startDate && accountStatementWithBalance.length > 0
			? new Date(formDetails.startDate)
			: new Date(
					accountStatementWithBalance.reduce(
						(min: Date, txn: Record<string, any>) =>
							new Date(txn?.createdAt) < new Date(min) ? txn?.createdAt : min,
						accountStatementWithBalance[0]?.createdAt,
					),
				);
	const dateTo =
		formDetails.endDate && accountStatementWithBalance.length > 0
			? new Date(formDetails.endDate)
			: new Date(
					accountStatementWithBalance.reduce(
						(max: Date, txn: Record<string, any>) =>
							new Date(txn?.createdAt) > new Date(max) ? txn?.createdAt : max,
						accountStatementWithBalance[0]?.createdAt,
					),
				);

	const printableStatement = (
		<Stack
			component="div"
			ref={contentRef}
			sx={{
				gap: "4rem",
				padding: "2rem",
				display: "none",
				background: "#FAFBFC",
				minHeight: "calc(100vh - 4rem)",
				"@media print": {
					display: "flex",
				},
			}}
		>
			<PrintStyles />
			<Stack
				gap={"2rem"}
				overflow={"hidden"}
				alignItems={"center"}
				direction={{ mobile: "row" }}
			>
				<Box
					overflow={"hidden"}
					sx={{
						"& .institution-logo": {
							width: "50px",
							height: "50px",
							borderRadius: "50%",
							objectFit: "cover",
						},
					}}
				>
					<img
						src={institution?.logo || baseLogo}
						alt="institution-logo"
						className="institution-logo"
					/>
				</Box>
				<Box flex={1} overflow={"hidden"} marginRight={"50px"}>
					<Typography
						fontFamily={"Poppins !important"}
						fontWeight={"700 !important"}
						fontSize={"32px !important"}
						lineHeight={"normal"}
						color="#000000"
						margin={"0 !important"}
						textAlign={"center"}
						marginBlockStart={"0 !important"}
						marginBlockEnd={"0.5rem"}
					>
						{institution?.name}
					</Typography>
					<Typography
						variant="body1"
						fontFamily={"Inter !important"}
						fontWeight={"400 !important"}
						fontSize={"14px !important"}
						lineHeight={"normal"}
						color="#000000"
						textAlign={"center"}
					>
						{institution?.address}
					</Typography>
				</Box>
			</Stack>
			<Stack
				gap={"2rem"}
				padding={"0 2rem"}
				overflow={"hidden"}
				direction={{ mobile: "row" }}
				justifyContent={"space-between"}
			>
				<Box overflow={"hidden"}>
					<Typography
						variant="subtitle1"
						fontFamily={"Poppins"}
						fontWeight={"700 !important"}
						fontSize={"14px !important"}
						lineHeight={"normal"}
						color="#000000"
						marginBlockEnd={"0.5rem"}
					>
						Report Title:{" "}
						<Typography
							component={"span"}
							fontFamily={"Inter !important"}
							fontWeight={"400 !important"}
							fontSize={"14px !important"}
							lineHeight={"normal"}
							color="#000000"
						>
							Account Statement
						</Typography>
					</Typography>
					<Typography
						variant="subtitle1"
						fontFamily={"Poppins !important"}
						fontWeight={"700 !important"}
						fontSize={"14px !important"}
						lineHeight={"normal"}
						color="#000000"
						marginBlockEnd={"0.5rem"}
					>
						Account No:{" "}
						<Typography
							component={"span"}
							fontFamily={"Inter !important"}
							fontWeight={"400 !important"}
							fontSize={"14px !important"}
							lineHeight={"normal"}
							color="#000000"
						>
							{customerAccountDetails?.accountNumber}
						</Typography>
					</Typography>
					<Typography
						variant="subtitle1"
						fontFamily={"Poppins !important"}
						fontWeight={"700 !important"}
						fontSize={"14px !important"}
						lineHeight={"normal"}
						color="#000000"
						marginBlockEnd={"0.5rem"}
					>
						Name:{" "}
						<Typography
							component={"span"}
							fontFamily={"Inter !important"}
							fontWeight={"400 !important"}
							fontSize={"14px !important"}
							lineHeight={"normal"}
							color="#000000"
						>
							{customerAccountDetails?.customerName}
						</Typography>
					</Typography>
					<Typography
						variant="subtitle1"
						fontFamily={"Poppins !important"}
						fontWeight={"700 !important"}
						fontSize={"14px !important"}
						lineHeight={"normal"}
						color="#000000"
						marginBlockEnd={"0.5rem"}
					>
						Branch:{" "}
						<Typography
							component={"span"}
							fontFamily={"Inter !important"}
							fontWeight={"400 !important"}
							fontSize={"14px !important"}
							lineHeight={"normal"}
							color="#000000"
						>
							{customerAccountDetails?.customerBranch}
						</Typography>
					</Typography>
					<Typography
						variant="subtitle1"
						fontFamily={"Poppins !important"}
						fontWeight={"700 !important"}
						fontSize={"14px !important"}
						lineHeight={"normal"}
						color="#000000"
					>
						Address:{" "}
						<Typography
							component={"span"}
							fontFamily={"Inter !important"}
							fontWeight={"400 !important"}
							fontSize={"14px !important"}
							lineHeight={"normal"}
							color="#000000"
						>
							{customerAccountDetails?.customerAddress}
						</Typography>
					</Typography>
				</Box>
				<Box overflow={"hidden"}>
					<Typography
						variant="subtitle1"
						fontFamily={"Poppins !important"}
						fontWeight={"700 !important"}
						fontSize={"14px !important"}
						lineHeight={"normal"}
						color="#000000"
						marginBlockEnd={"0.5rem"}
					>
						Balance BF:{" "}
						<Typography
							component={"span"}
							fontFamily={"Inter !important"}
							fontWeight={"400 !important"}
							fontSize={"14px !important"}
							lineHeight={"normal"}
							color="#000000"
						>
							{currency?.symbol || ""}
							{formatAmountDisplay(customerAccountDetails.balance)}
						</Typography>
					</Typography>
					<Typography
						variant="subtitle1"
						fontFamily={"Poppins !important"}
						fontWeight={"700 !important"}
						fontSize={"14px !important"}
						lineHeight={"normal"}
						color="#000000"
						marginBlockEnd={"0.5rem"}
					>
						Period:{" "}
						<Typography
							component={"span"}
							fontFamily={"Inter !important"}
							fontWeight={"400 !important"}
							fontSize={"14px !important"}
							lineHeight={"normal"}
							color="#000000"
						>
							{`${formatDate(dateFrom, false)} to ${formatDate(dateTo, false)}`}
						</Typography>
					</Typography>
					<Typography
						variant="subtitle1"
						fontFamily={"Poppins !important"}
						fontWeight={"700 !important"}
						fontSize={"14px !important"}
						lineHeight={"normal"}
						color="#000000"
						marginBlockEnd={"0.5rem"}
					>
						Remark:{" "}
						<Typography
							component={"span"}
							fontFamily={"Inter !important"}
							fontWeight={"400 !important"}
							fontSize={"14px !important"}
							lineHeight={"normal"}
							color="#000000"
						>
							{`${accountStatementWithBalance?.length} Transactions`}
						</Typography>
					</Typography>
					<Typography
						variant="subtitle1"
						fontFamily={"Poppins !important"}
						fontWeight={"700 !important"}
						fontSize={"14px !important"}
						lineHeight={"normal"}
						color="#000000"
					>
						Print Time:{" "}
						<Typography
							component={"span"}
							fontFamily={"Inter !important"}
							fontWeight={"400 !important"}
							fontSize={"14px !important"}
							lineHeight={"normal"}
							color="#000000"
						>
							{formatDate(new Date(), true)}
						</Typography>
					</Typography>
				</Box>
			</Stack>
			<Box
				padding={"0 2rem"}
				overflow={"hidden"}
				sx={{
					"& table": {
						background: "#FAFBFC",
						"& td, & th": {
							padding: "0.5rem 1rem !important",
						},
						"& thead": {
							"& th": {
								fontFamily: "Poppins !important",
								fontSize: "14px !important",
								fontWeight: "600 !important",
							},
							"& th:first-of-type": {
								maxWidth: "100px !important",
								paddingLeft: "0 !important",
							},
							"& th:last-of-type": {
								paddingRight: "0 !important",
							},
						},
						"& tbody": {
							"& tr": {
								"& td": {
									fontFamily: "Inter !important",
									fontSize: "14px !important",
									color: "#313A4E !important",
								},
								"& td:first-of-type": {
									maxWidth: "100px !important",
									color: "#000000 !important",
									paddingLeft: "0 !important",
								},
								"& td:last-of-type": {
									paddingRight: "0 !important",
								},
							},
						},
					},
				}}
			>
				<PrintStatementTable
					headers={printableStatementTableHeaders}
					rows={accountStatementWithBalance as Record<string, any>[]}
				/>
			</Box>
		</Stack>
	);

	return (
		<AppLayout pageId={"Print-Statement"} pageTitle={"Print Statement"}>
			<PrintStatementWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalCheckIcon />}
					title={`Account Statement Sent to Email`}
					message="Account statement has been successfully forwarded to requested email."
					callToAction="OK"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}

				{/* printable statement below */}
				{printableStatement}
				{/* printable statement above */}

				<form className="filter-bar">
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Search Customer Account
						</BaseLabel>
						<BaseInput
							required
							name="accountNumber"
							value={formDetails.accountNumber}
							placeholder="Enter Account Number"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Start Date</BaseLabel>
						<BaseInput
							type="date"
							name="startDate"
							fontweight={400}
							value={formDetails.startDate}
							colour="var(--semi-gray-color)"
							sx={{ width: "-webkit-fill-available" }}
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">End Date</BaseLabel>
						<BaseInput
							type="date"
							name="endDate"
							fontweight={400}
							value={formDetails.endDate}
							colour="var(--semi-gray-color)"
							sx={{ width: "-webkit-fill-available" }}
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</form>
				<Stack
					borderRadius={"16px"}
					gap={"var(--flex-gap)"}
					bgcolor={"var(--light-color)"}
					padding={"var(--basic-padding)"}
				>
					<Stack
						gap={"var(--flex-gap)"}
						direction={{ mobile: "column-reverse", laptop: "row" }}
					>
						<Card
							sx={{
								borderRadius: "1rem",
								flex: 0.4,
							}}
						>
							<CardContent className="card-content">
								<Stack
									height={"100%"}
									padding={"var(--basic-padding)"}
									gap={"var(--flex-gap)"}
								>
									<Box>
										{isLoadingCustomer ? (
											<CircularProgress color="inherit" className="loader" />
										) : (
											<Typography
												variant="h2"
												fontFamily={"Poppins"}
												fontWeight={600}
												fontSize={20}
												lineHeight={"normal"}
												color="var(--dark-color)"
											>
												{customerAccountDetails?.customerName}
											</Typography>
										)}
									</Box>
									<Stack>
										<Box>
											<Typography
												variant="subtitle1"
												fontFamily={"Poppins"}
												fontWeight={400}
												fontSize={16}
												lineHeight={"normal"}
												color="var(--primary-color)"
											>
												Account Balance:
											</Typography>
										</Box>
										<Box>
											{isLoadingCustomer ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												<Typography
													variant="body1"
													fontFamily={"Poppins"}
													fontWeight={600}
													fontSize={20}
													lineHeight={"normal"}
													color="var(--dark-color)"
												>
													{currency?.symbol || ""}
													{formatAmountDisplay(
														customerAccountDetails.balance ?? 0,
													)}
												</Typography>
											)}
										</Box>
									</Stack>
								</Stack>
							</CardContent>
						</Card>
						<Stack
							flex={0.6}
							overflow={"hidden"}
							gap={"calc(var(--flex-gap)/4)"}
							alignItems={{ laptop: "flex-end" }}
						>
							<Typography
								variant="h3"
								fontFamily={"Poppins"}
								fontWeight={600}
								fontSize={20}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								Savings Account Statement
							</Typography>
							<Typography
								variant="body1"
								fontFamily={"Poppins"}
								fontWeight={400}
								fontSize={16}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								Account Number:{" "}
								<Typography
									component="span"
									fontFamily={"Inter"}
									fontWeight={400}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color="inherit"
								>
									{customerAccountDetails?.accountNumber || "N/A"}
								</Typography>
							</Typography>
							<Typography
								variant="body1"
								fontFamily={"Poppins"}
								fontWeight={400}
								fontSize={16}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								Currency:{" "}
								<Typography
									component="span"
									fontFamily={"Inter"}
									fontWeight={400}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color="inherit"
								>
									{currency?.title || "N/A"}
								</Typography>
							</Typography>
						</Stack>
					</Stack>
					<Box component={"div"} className="print-statement-table-box">
						<Stack className="table-title">
							<Box overflow={"hidden"}>
								<Typography
									variant="h2"
									fontFamily={"Poppins"}
									fontWeight={500}
									fontSize={16}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									Statement of Account
								</Typography>
							</Box>
							<Box overflow={"hidden"}>
								<IconButton onClick={handleToggleTableVisibility}>
									{isTableVisible ? <VisibilityIcon /> : <NonVisibilityIcon />}
								</IconButton>
							</Box>
						</Stack>
						<Box display={isTableVisible ? "block" : "none"}>
							<PrintStatementTable
								headers={tableHeaders}
								rows={accountStatementWithBalance as Record<string, any>[]}
							/>
						</Box>
					</Box>
				</Stack>
				<Card
					sx={{
						borderRadius: "1rem",
						width: { laptop: "75%", xl: "50%" },
					}}
				>
					<CardContent className="card-content">
						<Stack
							height={"100%"}
							padding={"var(--basic-padding)"}
							gap={"var(--flex-gap)"}
						>
							<Box>
								<Typography
									variant="h3"
									fontFamily={"Poppins"}
									fontWeight={500}
									fontSize={18}
									lineHeight={"normal"}
									color="var(--dark-color)"
								>
									Statement Request
								</Typography>
							</Box>
							<form
								className="request-statement-form"
								onSubmit={handleSendAccountStatementToEmail}
							>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">Email</BaseLabel>
									<BaseInput
										required
										name="email"
										value={formDetails.email}
										placeholder="Enter Email"
										onChange={(e) => handleChange(e)}
									/>
								</BaseFieldSet>
								{error && (
									<Typography
										fontFamily={"Inter"}
										fontWeight={"600"}
										fontSize={14}
										lineHeight={"normal"}
										color={"var(--text-salmon-pink-color)"}
										whiteSpace={"normal"}
									>
										{error}
									</Typography>
								)}
								<Stack className="form-action-stack">
									<Box overflow={"hidden"}>
										<BaseButton
											type="submit"
											variant="contained"
											colour="var(--dark-color)"
											bgcolor="var(--disabled-grey-color)"
											disableElevation
											sx={{
												width: { mobile: "100%" },
											}}
											disabled={isLoading}
										>
											{isLoading ? (
												<CircularProgress color="inherit" className="loader" />
											) : (
												<Typography
													variant={"button"}
													fontFamily={"inherit"}
													fontWeight={"inherit"}
													fontSize={"inherit"}
													lineHeight={"inherit"}
													color={"inherit"}
													textTransform={"inherit"}
												>
													Request Statement
												</Typography>
											)}
										</BaseButton>
									</Box>
									<Box overflow={"hidden"}>
										<BaseButton
											variant="contained"
											colour="var(--light-color)"
											bgcolor="var(--sky-blue-color)"
											disableElevation
											sx={{
												width: { mobile: "100%" },
											}}
											disabled={isLoading}
											endIcon={<PrintIcon />}
											onClick={handlePrintAccountStatement}
										>
											<Typography
												variant={"button"}
												fontFamily={"inherit"}
												fontWeight={"inherit"}
												fontSize={"inherit"}
												lineHeight={"inherit"}
												color={"inherit"}
												textTransform={"inherit"}
											>
												Print Statement
											</Typography>
										</BaseButton>
									</Box>
								</Stack>
							</form>
						</Stack>
					</CardContent>
				</Card>
			</PrintStatementWrapper>
		</AppLayout>
	);
};
